import { Button } from "@/components/ui/button";
import type { StaffRoleValue } from "@/api/adminApi";
import type { ReactNode } from "react";

export type SuperAdminScreen =
  | "fields"
  | "create-field"
  | "field-usage"
  | "workflows"
  | "launch"
  | "organizations"
  | "users"
  | "responsibilities";

export const STAFF_ROLE_OPTIONS = [
  { value: "admin", label: "Organization Admin" },
  { value: "pm", label: "Program Manager" },
  { value: "ddo", label: "Application Reviewer" },
  { value: "management", label: "Management Approver" },
  { value: "finance", label: "Finance Reviewer" },
  { value: "pc", label: "Project Coordinator" },
] as const satisfies readonly { value: StaffRoleValue; label: string }[];

export const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "select", label: "Select list" },
  { value: "date", label: "Date" },
  { value: "file", label: "File upload" },
] as const;

export const FIELD_CATEGORIES = [
  "Eligibility",
  "Identity & verification",
  "School & institution",
  "Finance",
  "Contact",
] as const;

export const IMPORTANCE_OPTIONS = ["Low", "Medium", "High"] as const;

export type ApprovedFieldForm = {
  label: string;
  category: string;
  field_type: (typeof FIELD_TYPE_OPTIONS)[number]["value"];
  importance: (typeof IMPORTANCE_OPTIONS)[number];
  review_impact: string;
  description: string;
  why_needed: string;
};

export type OrganizationForm = {
  name: string;
  deactivate_reason: string;
};

export type StaffForm = {
  username: string;
  password: string;
  role: StaffRoleValue;
  email: string;
  phone: string;
  change_reason: string;
  reset_password: string;
};

export type ReviewProcessForm = {
  template_config_id: string;
  program_code: string;
  academic_year: string;
};

export type ScholarshipStandardForm = {
  template_code: string;
  description: string;
  preset:
    | "standard"
    | "interview"
    | "mentoring"
    | "interview_mentoring";
  workflow_template_id: string;
  funding_type: "single_year" | "multi_year";
  number_of_years: string;
  renewal_rule: string;
  mentoring_completion_criteria: string[];
  version_code: string;
  retire_reason: string;
  retire_confirm: boolean;
  edit_template_id?: number | null;
};

export function normalizeFieldKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function friendlyRole(role: string): string {
  return (
    STAFF_ROLE_OPTIONS.find((item) => item.value === role)?.label ??
    (role === "super_admin" ? "Platform Admin" : role)
  );
}

export function templateName(programCode: string): string {
  const normalized = programCode.replace(/_/g, " ").toLowerCase();
  if (normalized.includes("interview")) return "Scholarship With Interview";
  if (normalized.includes("client")) return "Scholarship With Client Governance";
  return "Standard Scholarship Review";
}

export function friendlyTemplateName(templateCode: string): string {
  const normalized = templateCode.replace(/_/g, " ").toLowerCase();
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function friendlyDate(value?: string | null): string {
  if (!value) return "Not recorded";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not recorded";
  return parsed.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function reviewPathSummary(config: {
  requires_interview: boolean;
  requires_client_governance: boolean;
  requires_management_approval: boolean;
}): string[] {
  const steps = ["Application review"];
  if (config.requires_interview) steps.push("Interview");
  if (config.requires_client_governance) steps.push("Client governance");
  if (config.requires_management_approval) steps.push("Management review");
  steps.push("Finance review");
  return steps;
}

export function duplicateWarnings(
  label: string,
  fields: { field_key: string; label: string; aliases?: string[] }[]
): string[] {
  const normalized = normalizeFieldKey(label);
  if (!normalized) return [];

  const semanticFamilies = [
    ["annual_income", "family_income", "parent_income", "income_amount", "household_income"],
    ["aadhaar_number", "aadhar_number", "student_aadhaar", "aadhaar"],
    ["bank_account_number", "account_number", "bank_account"],
    ["academic_score", "marks", "score", "percentage"],
  ];

  const warnings: string[] = [];
  fields.forEach((field) => {
    const keys = [field.field_key, normalizeFieldKey(field.label), ...(field.aliases ?? [])];
    if (keys.includes(normalized)) {
      warnings.push(`Exact match with existing field: ${field.label}`);
      return;
    }
    const matchingFamily = semanticFamilies.find((family) => family.includes(normalized));
    if (matchingFamily && keys.some((key) => matchingFamily.includes(key))) {
      warnings.push(`Likely duplicate of approved field: ${field.label}`);
    }
  });
  return Array.from(new Set(warnings));
}

export function Pill({
  children,
  tone = "slate",
}: {
  children: ReactNode;
  tone?: "green" | "blue" | "amber" | "red" | "teal" | "slate";
}) {
  const colors = {
    green: "bg-green-50 text-green-700 border-green-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    teal: "bg-teal-50 text-teal-700 border-teal-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${colors[tone]}`}>
      {children}
    </span>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-white px-4 py-3">
      <div>
        <h1 className="text-lg font-semibold text-gray-950">{title}</h1>
        <p className="text-xs text-gray-600">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-white p-5 text-sm">
      <div className="font-medium text-gray-950">{title}</div>
      <p className="mt-1 text-gray-600">{body}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export function MoreActions({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <details className="relative">
      <summary className="cursor-pointer list-none rounded-md border bg-white px-2 py-1 text-xs text-gray-700">
        More actions
      </summary>
      <div className="absolute right-0 z-20 mt-1 min-w-36 rounded-md border bg-white p-2 shadow-md">
        <div className="flex flex-col gap-2">{children}</div>
      </div>
    </details>
  );
}

export function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick}>
      Back
    </Button>
  );
}
